// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { ServiceGateways } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { NetworkManagementClient } from "../networkManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  ServiceGateway,
  ServiceGatewaysListAllNextOptionalParams,
  ServiceGatewaysListAllOptionalParams,
  ServiceGatewaysListAllResponse,
  ServiceGatewaysListNextOptionalParams,
  ServiceGatewaysListOptionalParams,
  ServiceGatewaysListResponse,
  ServiceGatewayAddressLocationResponse,
  ServiceGatewaysGetAddressLocationsNextOptionalParams,
  ServiceGatewaysGetAddressLocationsOptionalParams,
  ServiceGatewaysGetAddressLocationsResponse,
  ServiceGatewayService,
  ServiceGatewaysGetServicesNextOptionalParams,
  ServiceGatewaysGetServicesOptionalParams,
  ServiceGatewaysGetServicesResponse,
  ServiceGatewaysDeleteOptionalParams,
  ServiceGatewaysDeleteResponse,
  ServiceGatewaysGetOptionalParams,
  ServiceGatewaysGetResponse,
  ServiceGatewaysCreateOrUpdateOptionalParams,
  ServiceGatewaysCreateOrUpdateResponse,
  TagsObject,
  ServiceGatewaysUpdateTagsOptionalParams,
  ServiceGatewaysUpdateTagsResponse,
  ServiceGatewayUpdateAddressLocationsRequest,
  ServiceGatewaysUpdateAddressLocationsOptionalParams,
  ServiceGatewaysUpdateAddressLocationsResponse,
  ServiceGatewayUpdateServicesRequest,
  ServiceGatewaysUpdateServicesOptionalParams,
  ServiceGatewaysUpdateServicesResponse,
  ServiceGatewaysListAllNextResponse,
  ServiceGatewaysListNextResponse,
  ServiceGatewaysGetAddressLocationsNextResponse,
  ServiceGatewaysGetServicesNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing ServiceGateways operations. */
export class ServiceGatewaysImpl implements ServiceGateways {
  private readonly client: NetworkManagementClient;

  /**
   * Initialize a new instance of the class ServiceGateways class.
   * @param client Reference to the service client
   */
  constructor(client: NetworkManagementClient) {
    this.client = client;
  }

  /**
   * Gets all the service gateways in a subscription.
   * @param options The options parameters.
   */
  public listAll(
    options?: ServiceGatewaysListAllOptionalParams,
  ): PagedAsyncIterableIterator<ServiceGateway> {
    const iter = this.listAllPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listAllPagingPage(options, settings);
      },
    };
  }

  private async *listAllPagingPage(
    options?: ServiceGatewaysListAllOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<ServiceGateway[]> {
    let result: ServiceGatewaysListAllResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listAll(options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listAllNext(continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listAllPagingAll(
    options?: ServiceGatewaysListAllOptionalParams,
  ): AsyncIterableIterator<ServiceGateway> {
    for await (const page of this.listAllPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Gets all the service gateways in a resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    options?: ServiceGatewaysListOptionalParams,
  ): PagedAsyncIterableIterator<ServiceGateway> {
    const iter = this.listPagingAll(resourceGroupName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(resourceGroupName, options, settings);
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    options?: ServiceGatewaysListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<ServiceGateway[]> {
    let result: ServiceGatewaysListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(resourceGroupName, continuationToken, options);
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    options?: ServiceGatewaysListOptionalParams,
  ): AsyncIterableIterator<ServiceGateway> {
    for await (const page of this.listPagingPage(resourceGroupName, options)) {
      yield* page;
    }
  }

  /**
   * Get address locations in service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  public listAddressLocations(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetAddressLocationsOptionalParams,
  ): PagedAsyncIterableIterator<ServiceGatewayAddressLocationResponse> {
    const iter = this.getAddressLocationsPagingAll(resourceGroupName, serviceGatewayName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.getAddressLocationsPagingPage(
          resourceGroupName,
          serviceGatewayName,
          options,
          settings,
        );
      },
    };
  }

  private async *getAddressLocationsPagingPage(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetAddressLocationsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<ServiceGatewayAddressLocationResponse[]> {
    let result: ServiceGatewaysGetAddressLocationsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._getAddressLocations(resourceGroupName, serviceGatewayName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._getAddressLocationsNext(
        resourceGroupName,
        serviceGatewayName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *getAddressLocationsPagingAll(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetAddressLocationsOptionalParams,
  ): AsyncIterableIterator<ServiceGatewayAddressLocationResponse> {
    for await (const page of this.getAddressLocationsPagingPage(
      resourceGroupName,
      serviceGatewayName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Get Services in service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  public listServices(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetServicesOptionalParams,
  ): PagedAsyncIterableIterator<ServiceGatewayService> {
    const iter = this.getServicesPagingAll(resourceGroupName, serviceGatewayName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.getServicesPagingPage(resourceGroupName, serviceGatewayName, options, settings);
      },
    };
  }

  private async *getServicesPagingPage(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetServicesOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<ServiceGatewayService[]> {
    let result: ServiceGatewaysGetServicesResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._getServices(resourceGroupName, serviceGatewayName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._getServicesNext(
        resourceGroupName,
        serviceGatewayName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *getServicesPagingAll(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetServicesOptionalParams,
  ): AsyncIterableIterator<ServiceGatewayService> {
    for await (const page of this.getServicesPagingPage(
      resourceGroupName,
      serviceGatewayName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Deletes the specified service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysDeleteOptionalParams,
  ): Promise<
    SimplePollerLike<OperationState<ServiceGatewaysDeleteResponse>, ServiceGatewaysDeleteResponse>
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<ServiceGatewaysDeleteResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, serviceGatewayName, options },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<
      ServiceGatewaysDeleteResponse,
      OperationState<ServiceGatewaysDeleteResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Deletes the specified service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysDeleteOptionalParams,
  ): Promise<ServiceGatewaysDeleteResponse> {
    const poller = await this.beginDelete(resourceGroupName, serviceGatewayName, options);
    return poller.pollUntilDone();
  }

  /**
   * Gets the specified service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetOptionalParams,
  ): Promise<ServiceGatewaysGetResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, serviceGatewayName, options },
      getOperationSpec,
    );
  }

  /**
   * Creates or updates a service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or update service gateway operation.
   * @param options The options parameters.
   */
  async beginCreateOrUpdate(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGateway,
    options?: ServiceGatewaysCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<ServiceGatewaysCreateOrUpdateResponse>,
      ServiceGatewaysCreateOrUpdateResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<ServiceGatewaysCreateOrUpdateResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, serviceGatewayName, parameters, options },
      spec: createOrUpdateOperationSpec,
    });
    const poller = await createHttpPoller<
      ServiceGatewaysCreateOrUpdateResponse,
      OperationState<ServiceGatewaysCreateOrUpdateResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "azure-async-operation",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Creates or updates a service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or update service gateway operation.
   * @param options The options parameters.
   */
  async beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGateway,
    options?: ServiceGatewaysCreateOrUpdateOptionalParams,
  ): Promise<ServiceGatewaysCreateOrUpdateResponse> {
    const poller = await this.beginCreateOrUpdate(
      resourceGroupName,
      serviceGatewayName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Updates a service gateway tags.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to update service gateway tags.
   * @param options The options parameters.
   */
  updateTags(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: TagsObject,
    options?: ServiceGatewaysUpdateTagsOptionalParams,
  ): Promise<ServiceGatewaysUpdateTagsResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, serviceGatewayName, parameters, options },
      updateTagsOperationSpec,
    );
  }

  /**
   * Gets all the service gateways in a subscription.
   * @param options The options parameters.
   */
  private _listAll(
    options?: ServiceGatewaysListAllOptionalParams,
  ): Promise<ServiceGatewaysListAllResponse> {
    return this.client.sendOperationRequest({ options }, listAllOperationSpec);
  }

  /**
   * Gets all the service gateways in a resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    options?: ServiceGatewaysListOptionalParams,
  ): Promise<ServiceGatewaysListResponse> {
    return this.client.sendOperationRequest({ resourceGroupName, options }, listOperationSpec);
  }

  /**
   * Creates or updates address locations within the service gateway.
   *
   * The request supports both full and partial update modes at two levels: location and address.
   *
   * Full update replaces all existing data.
   *
   * Partial update modifies only the specified entries:
   *
   * For location-level partial updates, if no address is provided, the existing address will be deleted.
   *
   * For address-level partial updates, if no services are provided, the existing services will be
   * considered for deletion.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or updates address locations in service gateway
   *                   operation.
   * @param options The options parameters.
   */
  async beginUpdateAddressLocations(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGatewayUpdateAddressLocationsRequest,
    options?: ServiceGatewaysUpdateAddressLocationsOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<ServiceGatewaysUpdateAddressLocationsResponse>,
      ServiceGatewaysUpdateAddressLocationsResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<ServiceGatewaysUpdateAddressLocationsResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, serviceGatewayName, parameters, options },
      spec: updateAddressLocationsOperationSpec,
    });
    const poller = await createHttpPoller<
      ServiceGatewaysUpdateAddressLocationsResponse,
      OperationState<ServiceGatewaysUpdateAddressLocationsResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Creates or updates address locations within the service gateway.
   *
   * The request supports both full and partial update modes at two levels: location and address.
   *
   * Full update replaces all existing data.
   *
   * Partial update modifies only the specified entries:
   *
   * For location-level partial updates, if no address is provided, the existing address will be deleted.
   *
   * For address-level partial updates, if no services are provided, the existing services will be
   * considered for deletion.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or updates address locations in service gateway
   *                   operation.
   * @param options The options parameters.
   */
  async beginUpdateAddressLocationsAndWait(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGatewayUpdateAddressLocationsRequest,
    options?: ServiceGatewaysUpdateAddressLocationsOptionalParams,
  ): Promise<ServiceGatewaysUpdateAddressLocationsResponse> {
    const poller = await this.beginUpdateAddressLocations(
      resourceGroupName,
      serviceGatewayName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Creates, updates, or deletes services within the service gateway.
   * The request supports both full and partial update modes at the service level.
   *
   * Full update replaces all existing services with the new list provided in the request.
   * Partial update modifies only the specified services.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or updates services in service gateway
   *                   operation.
   * @param options The options parameters.
   */
  async beginUpdateServices(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGatewayUpdateServicesRequest,
    options?: ServiceGatewaysUpdateServicesOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<ServiceGatewaysUpdateServicesResponse>,
      ServiceGatewaysUpdateServicesResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<ServiceGatewaysUpdateServicesResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, serviceGatewayName, parameters, options },
      spec: updateServicesOperationSpec,
    });
    const poller = await createHttpPoller<
      ServiceGatewaysUpdateServicesResponse,
      OperationState<ServiceGatewaysUpdateServicesResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Creates, updates, or deletes services within the service gateway.
   * The request supports both full and partial update modes at the service level.
   *
   * Full update replaces all existing services with the new list provided in the request.
   * Partial update modifies only the specified services.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param parameters Parameters supplied to the create or updates services in service gateway
   *                   operation.
   * @param options The options parameters.
   */
  async beginUpdateServicesAndWait(
    resourceGroupName: string,
    serviceGatewayName: string,
    parameters: ServiceGatewayUpdateServicesRequest,
    options?: ServiceGatewaysUpdateServicesOptionalParams,
  ): Promise<ServiceGatewaysUpdateServicesResponse> {
    const poller = await this.beginUpdateServices(
      resourceGroupName,
      serviceGatewayName,
      parameters,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * Get address locations in service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  private _getAddressLocations(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetAddressLocationsOptionalParams,
  ): Promise<ServiceGatewaysGetAddressLocationsResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, serviceGatewayName, options },
      getAddressLocationsOperationSpec,
    );
  }

  /**
   * Get Services in service gateway.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param options The options parameters.
   */
  private _getServices(
    resourceGroupName: string,
    serviceGatewayName: string,
    options?: ServiceGatewaysGetServicesOptionalParams,
  ): Promise<ServiceGatewaysGetServicesResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, serviceGatewayName, options },
      getServicesOperationSpec,
    );
  }

  /**
   * ListAllNext
   * @param nextLink The nextLink from the previous successful call to the ListAll method.
   * @param options The options parameters.
   */
  private _listAllNext(
    nextLink: string,
    options?: ServiceGatewaysListAllNextOptionalParams,
  ): Promise<ServiceGatewaysListAllNextResponse> {
    return this.client.sendOperationRequest({ nextLink, options }, listAllNextOperationSpec);
  }

  /**
   * ListNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    nextLink: string,
    options?: ServiceGatewaysListNextOptionalParams,
  ): Promise<ServiceGatewaysListNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, nextLink, options },
      listNextOperationSpec,
    );
  }

  /**
   * GetAddressLocationsNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param nextLink The nextLink from the previous successful call to the GetAddressLocations method.
   * @param options The options parameters.
   */
  private _getAddressLocationsNext(
    resourceGroupName: string,
    serviceGatewayName: string,
    nextLink: string,
    options?: ServiceGatewaysGetAddressLocationsNextOptionalParams,
  ): Promise<ServiceGatewaysGetAddressLocationsNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, serviceGatewayName, nextLink, options },
      getAddressLocationsNextOperationSpec,
    );
  }

  /**
   * GetServicesNext
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param serviceGatewayName The name of the service gateway.
   * @param nextLink The nextLink from the previous successful call to the GetServices method.
   * @param options The options parameters.
   */
  private _getServicesNext(
    resourceGroupName: string,
    serviceGatewayName: string,
    nextLink: string,
    options?: ServiceGatewaysGetServicesNextOptionalParams,
  ): Promise<ServiceGatewaysGetServicesNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, serviceGatewayName, nextLink, options },
      getServicesNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/serviceGateways/{serviceGatewayName}",
  httpMethod: "DELETE",
  responses: {
    200: {
      headersMapper: Mappers.ServiceGatewaysDeleteHeaders,
    },
    201: {
      headersMapper: Mappers.ServiceGatewaysDeleteHeaders,
    },
    202: {
      headersMapper: Mappers.ServiceGatewaysDeleteHeaders,
    },
    204: {
      headersMapper: Mappers.ServiceGatewaysDeleteHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName1,
    Parameters.serviceGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/serviceGateways/{serviceGatewayName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ServiceGateway,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName1,
    Parameters.serviceGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOrUpdateOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/serviceGateways/{serviceGatewayName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.ServiceGateway,
    },
    201: {
      bodyMapper: Mappers.ServiceGateway,
    },
    202: {
      bodyMapper: Mappers.ServiceGateway,
    },
    204: {
      bodyMapper: Mappers.ServiceGateway,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters82,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName1,
    Parameters.serviceGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const updateTagsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/serviceGateways/{serviceGatewayName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.ServiceGateway,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters1,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName1,
    Parameters.serviceGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const listAllOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Network/serviceGateways",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ServiceGatewayListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId],
  headerParameters: [Parameters.accept],
  serializer,
};
const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/serviceGateways",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ServiceGatewayListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId, Parameters.resourceGroupName1],
  headerParameters: [Parameters.accept],
  serializer,
};
const updateAddressLocationsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/serviceGateways/{serviceGatewayName}/updateAddressLocations",
  httpMethod: "POST",
  responses: {
    200: {
      headersMapper: Mappers.ServiceGatewaysUpdateAddressLocationsHeaders,
    },
    201: {
      headersMapper: Mappers.ServiceGatewaysUpdateAddressLocationsHeaders,
    },
    202: {
      headersMapper: Mappers.ServiceGatewaysUpdateAddressLocationsHeaders,
    },
    204: {
      headersMapper: Mappers.ServiceGatewaysUpdateAddressLocationsHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters83,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName1,
    Parameters.serviceGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const updateServicesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/serviceGateways/{serviceGatewayName}/updateServices",
  httpMethod: "POST",
  responses: {
    200: {
      headersMapper: Mappers.ServiceGatewaysUpdateServicesHeaders,
    },
    201: {
      headersMapper: Mappers.ServiceGatewaysUpdateServicesHeaders,
    },
    202: {
      headersMapper: Mappers.ServiceGatewaysUpdateServicesHeaders,
    },
    204: {
      headersMapper: Mappers.ServiceGatewaysUpdateServicesHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  requestBody: Parameters.parameters84,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName1,
    Parameters.serviceGatewayName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const getAddressLocationsOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/serviceGateways/{serviceGatewayName}/addressLocations",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.GetServiceGatewayAddressLocationsResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName1,
    Parameters.serviceGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getServicesOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/serviceGateways/{serviceGatewayName}/services",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.GetServiceGatewayServicesResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.resourceGroupName1,
    Parameters.serviceGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listAllNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ServiceGatewayListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [Parameters.$host, Parameters.subscriptionId, Parameters.nextLink],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ServiceGatewayListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.resourceGroupName1,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getAddressLocationsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.GetServiceGatewayAddressLocationsResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.resourceGroupName1,
    Parameters.serviceGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getServicesNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.GetServiceGatewayServicesResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.resourceGroupName1,
    Parameters.serviceGatewayName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
